import { AxiosError } from 'axios';
import { FormikErrors } from 'formik';
import i18next from 'i18next';
import {
  forEach,
  includes,
  isArray,
  isEmpty,
  isNumber,
  isString,
  keys,
  snakeCase,
} from 'lodash';
import * as Yup from 'yup';

import { ERROR_MESSAGE } from 'constants/common.constants';
import { StatusCode } from 'enums';
import { ValidationError } from 'types';

import { formatUtils } from './format.utils';

const validators = {
  uuid: (value: string) =>
    !Yup.string().uuid().isValidSync(value) && value
      ? i18next.t('errors.invalid_uuid')
      : undefined,
} as const;

type ErrorOverrides<T = any> = {
  [property in keyof T]: {
    [constraint: string]: string;
  };
};

const getConstraintTKey = (constraint: string) => {
  switch (constraint) {
    case 'isEmail':
      return 'email';
    default:
      return snakeCase(constraint);
  }
};

// Untranslated constraints resolve to undefined so that the caller decides what to show:
// a form field falls back to the raw backend text, a toast falls back to the generic message.
const getConstraintTranslate = (
  constraint: string,
  error: ValidationError,
  overrides?: ErrorOverrides,
) => {
  const override = overrides?.[error.property]?.[constraint];
  if (override) {
    return override;
  }
  if (constraint === 'max') {
    const lastSpace = error.constraints?.[constraint].lastIndexOf(' ');
    const maxValue = error.constraints?.[constraint].slice(lastSpace) || 0;
    return `${i18next.t(`errors.${constraint}`)} ${formatUtils.formatMoney(
      +maxValue,
    )}`;
  }
  if (constraint === 'too_big' && error.property === 'amount') {
    return i18next.t(`errors.not_enough_money`);
  }
  if (
    constraint === 'bad_currencies_spread' &&
    error.property === 'fiatCurrencyId'
  ) {
    return i18next.t(`errors.bad_currencies_spread`);
  }
  const tKey = getConstraintTKey(constraint);
  return i18next.exists(`errors.${tKey}_${error.property}`)
    ? i18next.t(`errors.${tKey}_${error.property}` as any)
    : i18next.exists(`errors.${tKey}` as any)
    ? i18next.t(`errors.${tKey}` as any)
    : undefined;
};

const getValidationErrorMessage = (
  error: ValidationError,
  overrides?: ErrorOverrides,
) => {
  const constraint = keys(error?.constraints)?.[0];
  return getConstraintTranslate(constraint, error, overrides);
};

const getRawConstraintMessage = (error: ValidationError) =>
  error.constraints?.[keys(error?.constraints)?.[0]];

const getFormikErrors = <Values = any>(
  validationErrors: ValidationError[],
  overrides?: ErrorOverrides<Values>,
) => {
  const errors: FormikErrors<any> = {};
  if (isArray(validationErrors)) {
    forEach(validationErrors, (error) => {
      if (error.children && !isEmpty(error.children)) {
        errors[error.property] = getFormikErrors(error.children);
      } else {
        errors[error.property] =
          getValidationErrorMessage(error, overrides) ??
          getRawConstraintMessage(error);
      }
    });
  }

  return errors;
};

const findConstrainedError = (
  validationErrors: ValidationError[],
): ValidationError | undefined => {
  let found: ValidationError | undefined;
  forEach(validationErrors, (error) => {
    if (!isEmpty(error?.constraints)) {
      found = error;
    } else if (!isEmpty(error?.children)) {
      found = findConstrainedError(error.children as ValidationError[]);
    }
    return !found;
  });
  return found;
};

// The UI ships ru locale only, so an untranslated backend message would surface as raw English.
// Returning undefined keeps the localized generic error instead.
const getServerMessageTranslate = (message: string) => {
  const tKey = `errors.server.${snakeCase(message)}`;
  return i18next.exists(tKey as any) ? i18next.t(tKey as any) : undefined;
};

const getServerErrorMessage = (error: AxiosError): string | undefined => {
  const data = error?.response?.data as
    | { message?: ValidationError[] | string; errorMessage?: string }
    | undefined;
  if (!data) {
    return undefined;
  }

  if (isArray(data.message)) {
    const validationError = findConstrainedError(data.message);
    return validationError
      ? getValidationErrorMessage(validationError)
      : undefined;
  }

  const message = isString(data.message) ? data.message : data.errorMessage;
  return message ? getServerMessageTranslate(message) : undefined;
};

const getFormErrors = <Values = any>(
  error: AxiosError,
  overrides?: ErrorOverrides<Values>,
) => {
  const validationErrors = (error as AxiosError<{ message: ValidationError[] }>)
    ?.response?.data.message;

  let errors: FormikErrors<any> = {};
  if (validationErrors) {
    errors = getFormikErrors(validationErrors, overrides);
  }

  return errors as FormikErrors<Values>;
};

const isTwoFAError = (error: AxiosError<any>) =>
  includes(error?.response?.data?.message, '2FA');

const isSecurePasswordConfirmError = (error: AxiosError<any>) =>
  error?.response?.data?.message ===
  ERROR_MESSAGE.PASSWORD_CONFIRM_VERIFICATION_FAILED;

const isEntityInUseError = (error: AxiosError<any>) =>
  error?.response?.status === StatusCode.Conflict &&
  error.response?.data?.message === ERROR_MESSAGE.ENTITY_IN_USE;

const isTradeMethodNotFoundError = (error: AxiosError<any>) =>
  error?.response?.data?.message === 'Trade method not found';

const getAmountError = (error: AxiosError<any>) => {
  const status = error?.response?.status;
  const message = error.response?.data?.message?.[0] as any;
  return status === StatusCode.BadRequest && message?.property === 'amount'
    ? message.target
    : null;
};

const getNaturalNumberFieldValidationSchema = (
  options: {
    required?: boolean;
    integer?: boolean;
    additionalSchema?: Yup.NumberSchema;
  } = {},
) => {
  const baseSchema = options.required
    ? Yup.number()
        .min(0, i18next.t('errors.natural_or_zero_number'))
        .required(i18next.t('errors.required'))
    : Yup.number()
        .min(0, i18next.t('errors.natural_or_zero_number'))
        .nullable();
  const schema = options.integer
    ? baseSchema.integer(i18next.t('errors.natural_or_zero_number'))
    : baseSchema;
  if (!options.additionalSchema) {
    return schema;
  }
  // Yup's concat() resets nullable/required to the concatenated schema's
  // defaults, so re-apply them afterwards or a cleared field casts to NaN
  // and fails with Yup's untranslated built-in type error.
  const concatenatedSchema = schema.concat(options.additionalSchema);
  return options.required
    ? concatenatedSchema.required(i18next.t('errors.required'))
    : concatenatedSchema.nullable();
};

const getNaturalNumberRangeValidationSchema = (
  minFieldName: string,
  maxFieldName: string,
  required?: boolean,
) => ({
  [minFieldName]: getNaturalNumberFieldValidationSchema({ required }),
  [maxFieldName]: getNaturalNumberFieldValidationSchema({
    required,
    additionalSchema: Yup.number().test(
      'validRange',
      i18next.t('errors.more_than_min'),
      function (maxValue) {
        const minValue = this.resolve(Yup.ref(minFieldName)) as number;
        if (required) {
          return (
            !isNumber(minValue) || (isNumber(maxValue) && maxValue >= minValue)
          );
        } else {
          return !maxValue || !minValue || maxValue >= minValue;
        }
      },
    ),
  }),
});

const isValidRegexPattern = (pattern: string): boolean => {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
};

export const validationUtils = {
  getFormErrors,
  getServerErrorMessage,
  validators,
  isTwoFAError,
  isSecurePasswordConfirmError,
  isEntityInUseError,
  isTradeMethodNotFoundError,
  getNaturalNumberFieldValidationSchema,
  getNaturalNumberRangeValidationSchema,
  isValidRegexPattern,
  getAmountError,
};
